import { Box, makeStyles, styled, Theme } from '@material-ui/core';
import React from 'react';
import Carousel from 'react-material-ui-carousel';
import Button from '../Button';

interface CarouselLayoutProps {
  length: number;
  nextEnabled?: (currentStep: number) => boolean | string;
  currentFrame?: number;
  hiddenPrev?: boolean;
  hiddenNext?: boolean;
  hideAllActions?: boolean;
  finalActionButton?: React.ReactNode;
  carouselSecondaryAction?: React.ReactNode;
}

interface CarouselLayoutStyleProps {
  hideAllActions: boolean;
}

interface MuiCarouselStyleProps extends CarouselLayoutStyleProps {
  index: number;
  nextEnabled?: boolean;
  theme: Theme;
}

const StyledCarousel = styled(Carousel)(
  ({ index, nextEnabled, theme }: MuiCarouselStyleProps) => ({
    '&& .MuiSvgIcon-root': {
      fontSize: 11,
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
      [`&:not(:nth-child(-n+${Number(index + 1)}))`]: {
        pointerEvents: nextEnabled ? 'auto' : 'none',
      },
      [`&:nth-child(-n+${Number(index + 1)})`]: {
        pointerEvents: 'auto',
      },
    },
  }),
);

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  indicatorContainer: {
    marginTop: (props: CarouselLayoutStyleProps) =>
      props.hideAllActions ? theme.spacing(4) : theme.spacing(8),
  },
  carouselActionWrapper: {
    position: 'absolute',
    width: '100%',
    zIndex: 1,
    top: '60%',
    [theme.breakpoints.down('xs')]: {
      top: '50%',
    },
  },
}));

const CarouselLayout: React.FC<CarouselLayoutProps> = ({
  children,
  length,
  currentFrame,
  hiddenPrev = false,
  hiddenNext = false,
  hideAllActions = false,
  nextEnabled,
  finalActionButton,
  carouselSecondaryAction,
}) => {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const nextEnabledValue = nextEnabled && nextEnabled(currentIndex);
  const classes = useStyles({ hideAllActions });
  React.useEffect(() => {
    if (currentFrame !== undefined) setCurrentIndex(currentFrame);
  }, [currentFrame]);

  const handleChange = (index: number) => {
    if (index !== currentIndex) {
      setCurrentIndex(index);
    }
  };

  const handleNext = () => {
    setCurrentIndex(currentIndex + 1);
  };

  const renderActionButtons = () => {
    if (hideAllActions) {
      return null;
    }

    return (
      <Box className={classes.carouselActionWrapper}>
        {currentIndex !== 0 && !hiddenPrev && (
          <Button
            size="large"
            htmlId="register-prev-btn"
            fullWidth
            color="primary"
            variant="contained"
          >
            Prev
          </Button>
        )}
        {currentIndex !== length - 1 && !hiddenNext && (
          <Button
            size="large"
            htmlId="register-next-btn"
            fullWidth
            color="primary"
            variant="contained"
            disabled={!nextEnabledValue}
            onClick={handleNext}
          >
            Next
          </Button>
        )}
        {currentIndex === length - 1 && finalActionButton && (
          <Box>{finalActionButton}</Box>
        )}

        {carouselSecondaryAction}
      </Box>
    );
  };

  return (
    <Box position="relative" height={1}>
      <StyledCarousel
        classes={{ root: classes.root }}
        index={currentIndex}
        nextEnabled={nextEnabledValue}
        onChange={handleChange}
        navButtonsAlwaysInvisible
        autoPlay={false}
        indicatorContainerProps={{
          className: classes.indicatorContainer,
          style: {},
        }}
      >
        {children}
      </StyledCarousel>
      {renderActionButtons()}
    </Box>
  );
};

export default CarouselLayout;
